/**
 * Les coordonnées de facturation des agrégateurs (item H1).
 *
 * C'est le défaut que David a signalé le 26/08 : il avait créé deux factures et ne
 * pouvait renseigner aucune coordonnée du destinataire. Le PDF disait « Facture à :
 * Agrégateur Symphonics » et s'arrêtait là.
 *
 * Une fiche par agrégateur, servie par le serveur même quand elle est vide : l'écran
 * doit pouvoir proposer la saisie AVANT qu'elle existe. Ce qui manque est signalé, pas
 * bloqué — un opérateur remplit sa fiche quand il obtient les informations.
 */
import { useEffect, useState } from 'react';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Building2, Save, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import Modale from './Modale';

// Les deux premiers sont indispensables : sans eux la facture ne dit pas à qui elle
// s'adresse. Le serveur applique la même règle, cet ordre ne fait que la rendre lisible.
const CHAMPS = [
  { cle: 'raison_sociale', label: 'Raison sociale *', large: true, exemple: 'Symphonics Énergie SAS' },
  { cle: 'adresse', label: 'Adresse *', large: true, exemple: "12 rue de l'Agrégation" },
  { cle: 'code_postal', label: 'Code postal', exemple: '75009' },
  { cle: 'ville', label: 'Ville', exemple: 'Paris' },
  { cle: 'pays', label: 'Pays', exemple: 'France' },
  { cle: 'siret', label: 'SIRET', exemple: '84512378900021' },
  { cle: 'tva_num', label: 'N° TVA intracommunautaire', exemple: 'FR40845123789' },
  { cle: 'contact_nom', label: 'Contact facturation', exemple: 'Service comptabilité' },
  { cle: 'contact_email', label: 'Email de facturation', exemple: 'compta@exemple.fr' },
  { cle: 'reference_commande', label: 'Référence commande', exemple: 'BC-2026-118' },
];

export default function CoordonneesDestinataires({ onClose }) {
  const [fiches, setFiches] = useState([]);
  const [selection, setSelection] = useState('');
  const [brouillon, setBrouillon] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/factures/destinataires');
        setFiches(r.data || []);
        if (r.data?.length) {
          setSelection(r.data[0].agregateur_cle);
          setBrouillon({ ...r.data[0] });
        }
      } catch {
        toast.error('Impossible de charger les agrégateurs');
      }
      setChargement(false);
    })();
  }, []);

  const choisir = (cle) => {
    setSelection(cle);
    setBrouillon({ ...fiches.find((f) => f.agregateur_cle === cle) });
  };

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      const corps = Object.fromEntries(CHAMPS.map((c) => [c.cle, brouillon[c.cle] || '']));
      const r = await api.put(`/factures/destinataires/${selection}`, corps);
      setFiches((liste) => liste.map((f) => (f.agregateur_cle === selection ? r.data : f)));
      setBrouillon({ ...r.data });
      toast.success('Coordonnées enregistrées');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setEnregistrement(false);
  };

  return (
    <Modale
      titre="Coordonnées de facturation"
      icone={Building2}
      taille="large"
      onClose={onClose}
      pied={
        <>
          <Button variant="ghost" onClick={onClose}>Fermer</Button>
          <Button onClick={enregistrer} disabled={enregistrement || !brouillon} data-testid="destinataire-enregistrer">
            <Save className="w-4 h-4 mr-1.5" />
            {enregistrement ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      {chargement ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
      ) : (
        <>
          <p className="text-[12.5px] text-neutral-500">
            Ces informations figurent sur la facture, dans le bloc « Facturer à ». Elles sont
            recopiées sur la facture au moment de la validation : la corriger ici ne modifie
            aucune facture déjà émise.
          </p>

          <div className="flex flex-wrap gap-1.5" data-testid="destinataire-onglets">
            {fiches.map((f) => (
              <button
                key={f.agregateur_cle}
                onClick={() => choisir(f.agregateur_cle)}
                className={`px-3 py-1.5 rounded-md text-[12.5px] border flex items-center gap-1.5 ${
                  selection === f.agregateur_cle
                    ? 'border-neutral-800 bg-neutral-900 text-white'
                    : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                }`}
                data-testid={`destinataire-onglet-${f.agregateur_cle}`}
              >
                {f.incomplet ? (
                  <AlertCircle className={`w-3.5 h-3.5 ${selection === f.agregateur_cle ? 'text-amber-300' : 'text-amber-500'}`} />
                ) : (
                  <CheckCircle2 className={`w-3.5 h-3.5 ${selection === f.agregateur_cle ? 'text-emerald-300' : 'text-emerald-500'}`} />
                )}
                {f.agregateur_nom}
              </button>
            ))}
          </div>

          {brouillon && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {CHAMPS.map((champ) => (
                <div key={champ.cle} className={champ.large ? 'md:col-span-2' : ''}>
                  <Label className="text-[11px]">{champ.label}</Label>
                  <Input
                    value={brouillon[champ.cle] || ''}
                    placeholder={champ.exemple}
                    onChange={(e) => setBrouillon({ ...brouillon, [champ.cle]: e.target.value })}
                    className="mt-1 h-9"
                    data-testid={`destinataire-${champ.cle}`}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Modale>
  );
}
